import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, RefreshCw, AlignLeft, AlignCenter, Minus } from "lucide-react";
import { speechService } from "../../services/speechService";
import { notification } from "antd";

interface SmartEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRecordingId: string;
  currentOptions: {
    patient_name: string;
    visit_type: string;
    pronouns: string;
    note_length: string;
    template_id: string;
  };
  onReprocessSuccess: (formData: any) => void;
  onError: (error: string) => void;
}

export const SmartEditModal: React.FC<SmartEditModalProps> = ({
  isOpen,
  onClose,
  selectedRecordingId,
  currentOptions,
  onReprocessSuccess,
  onError,
}) => {
  const [instructions, setInstructions] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [selectedNoteLength, setSelectedNoteLength] = useState(currentOptions.note_length || "standard");
  const [selectedPronouns, setSelectedPronouns] = useState(currentOptions.pronouns || "");
  const [selectedVisitType, setSelectedVisitType] = useState(currentOptions.visit_type || "New Patient");
  const [isProcessing, setIsProcessing] = useState(false);

  const noteLengthOptions = [
    { value: "detailed", label: "Detailed", icon: <AlignLeft className="w-5 h-5" /> },
    { value: "standard", label: "Standard", icon: <AlignCenter className="w-5 h-5" /> },
    { value: "concise", label: "Concise", icon: <Minus className="w-5 h-5" /> },
  ];

  const pronounOptions = [
    { value: "She/Her", label: "She/Her" },
    { value: "He/Him", label: "He/Him" },
    { value: "They/Them", label: "They/Them" },
  ];

  const visitTypeOptions = [
    { value: "New Patient", label: "New Patient" },
    { value: "Follow Up", label: "Follow Up" },
    { value: "Annual Physical", label: "Annual Physical" },
    { value: "Consultation", label: "Consultation" },
  ];

  const handleRegenerate = async () => {
    if (!selectedRecordingId) return;

    setIsProcessing(true);

    try {
      const updatedOptions = {
        patient_name: currentOptions.patient_name,
        visit_type: selectedVisitType,
        pronouns: selectedPronouns,
        note_length: selectedNoteLength,
        instructions: instructions.trim() || undefined,
        additional_context: additionalContext.trim() || undefined,
      };

      // Call the retry-process API
      await speechService.retryProcess(
        selectedRecordingId,
        currentOptions.template_id,
        updatedOptions
      );

      // Show success message
      notification.success({
        message: "Smart Edit Started!",
        description: "Your recording is being processed with the new formatting rules and context.",
        placement: "topRight",
        duration: 3
      });

      // Notify parent of success
      onReprocessSuccess(updatedOptions);
      
      // Close modal
      onClose();

      // Refresh the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error("Error in Smart Edit:", error);
      notification.error({
        message: "Smart Edit Failed",
        description: "There was an error processing your Smart Edit request. Please try again.",
        placement: "topRight",
        duration: 5
      });
      onError("Failed to process Smart Edit");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#00000040] backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg sm:max-w-xl max-h-[95vh] sm:max-h-[85vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-[#F2F2F7]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-[#1D1D1F]">Smart Edit</h2>
              <p className="text-[#86868B] mt-1 text-sm">
                Create an updated version of the scribe note by adding patient details, encounter specifics, or applying custom formatting.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F5F5F7] rounded-full transition-colors flex-shrink-0 ml-3"
            >
              <X className="w-4 h-4 text-[#86868B]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(95vh-220px)] sm:max-h-[calc(85vh-200px)]">
          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
              Instructions (Optional)
              <span className="float-right text-[#86868B] text-xs">{instructions.length}/4000</span>
            </label>
            <div className="bg-[#F5F5F7] rounded-xl p-1">
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value.slice(0, 4000))}
                placeholder='Enter specific rules to apply to this Scribe such as "use bullet points" or "be more concise"'
                className="w-full h-20 sm:h-24 px-3 py-2.5 bg-transparent border-none focus:outline-none resize-none text-sm leading-relaxed text-[#1D1D1F] placeholder:text-[#86868B]"
                style={{ fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif" }}
              />
            </div>
          </div>

          {/* Additional Context */}
          <div>
            <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
              Additional Context (Optional)
              <span className="float-right text-[#86868B] text-xs">{additionalContext.length}/4000</span>
            </label>
            <div className="bg-[#F5F5F7] rounded-xl p-1">
              <textarea
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value.slice(0, 4000))}
                placeholder="Enter additional info and details for this session"
                className="w-full h-24 sm:h-32 px-3 py-3 bg-transparent border-none focus:outline-none resize-none text-sm leading-relaxed text-[#1D1D1F] placeholder:text-[#86868B]"
                style={{ fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif" }}
              />
            </div>
          </div>

          {/* Note Length */}
          <div>
            <label className="block text-sm font-medium text-[#1D1D1F] mb-3">Note Length</label>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {noteLengthOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedNoteLength(option.value)}
                  className={`p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 ${
                    selectedNoteLength === option.value
                      ? "border-[#0071E3] bg-[#0071E3]/10"
                      : "border-[#E5E5EA] hover:border-[#86868B] hover:shadow-sm"
                  }`}
                >
                  <div className="mb-1 sm:mb-2 text-[#1D1D1F] flex items-center justify-center">{option.icon}</div>
                  <div className="text-xs sm:text-sm font-medium text-[#1D1D1F]">{option.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Pronouns */}
            <div>
              <label className="block text-sm font-medium text-[#1D1D1F] mb-3">Pronouns</label>
              <div className="space-y-2">
                {pronounOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedPronouns(option.value)}
                    className={`w-full p-3 rounded-xl border text-left transition-all duration-200 text-sm font-medium ${
                      selectedPronouns === option.value
                        ? "border-[#5856D6] bg-[#5856D6]/10 text-[#5856D6]"
                        : "border-[#E5E5EA] hover:border-[#86868B] hover:shadow-sm text-[#1D1D1F]"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Visit Type */}
            <div>
              <label className="block text-sm font-medium text-[#1D1D1F] mb-3">Visit Type</label>
              <div className="space-y-2">
                {visitTypeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedVisitType(option.value)}
                    className={`w-full p-3 rounded-xl border text-left transition-all duration-200 text-sm font-medium ${
                      selectedVisitType === option.value
                        ? "border-[#34C759] bg-[#34C759]/10 text-[#34C759]"
                        : "border-[#E5E5EA] hover:border-[#86868B] hover:shadow-sm text-[#1D1D1F]"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-[#F2F2F7]">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-medium text-[#1D1D1F] border border-[#E5E5EA] hover:bg-[#F5F5F7] rounded-xl transition-all duration-200 hover:shadow-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleRegenerate}
              disabled={isProcessing}
              className="flex-1 inline-flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:from-blue-800 active:to-blue-900 rounded-xl transition-all duration-200 shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>Regenerate</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-[#86868B] text-center">
            To use these rules for future Scribes, add them as Custom Formatting in{" "}
            <span className="text-[#0071E3] font-medium">AI Studio</span>
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}; 